"use client";

import { useSelector, useDispatch } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "next/navigation";

import styles from "./assignments.module.scss";
import services from "@/store/services";
import useApi from "@/hooks/useApi";
import sk from "@/skeletons";
import api from "@/api";

function Assignments() {
  const { isRestricted, isEnrolled, courseInfo, videoInfo } = useSelector(
    (state) => state.video
  );

  const params = useParams();
  const { slug } = params;

  const user = useSelector((state) => state?.user?.users);

  const [submittedAssignment, setSubmittedAssignment] = useState(null);
  const [assignmentValue, setAssignmentValue] = useState("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [showEdit, setShowEdit] = useState(true);

  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState({
    watch: 0,
    pending: 0,
    assignment: 0,
    total: 0,
  });

  const { data, request: getassignment } = useApi(api.getassignment);

  const dispatch = useDispatch();

  const getResponsesOnAssignments = useCallback(async () => {
    setLoading(true);
    if (videoInfo) await getassignment(videoInfo._id);
    // Is course restricted || Enrolled
    if (isRestricted || isEnrolled) {
      const submissionRes = await api.getsubmissionbyme(videoInfo._id);
      const courseScore = await api.getcoursescore(courseInfo?._id);
      if (submissionRes.data?.success === 200) {
        setSubmittedAssignment(submissionRes.data?.result?.submissionLink);
        setShowEdit(submissionRes.data.result.score >= 10 ? false : true);
        setRemarks(submissionRes.data.result.remarks);
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
      if (courseScore?.data) {
        setScores({
          watch: courseScore.data.videoScores,
          pending: courseScore.data.pendingCount,
          assignment: courseScore.data.assignmentScores,
          total: courseScore.data.totalScore,
        });
      }
    }
    setLoading(false);
  }, [user, slug]);

  useEffect(() => {
    getResponsesOnAssignments();
  }, [getResponsesOnAssignments]);

  const handleSubmit = async (e) => {
    //make the submit assignment api call here
    e.preventDefault();

    const onSuccess = () => {
      setSubmittedAssignment(assignmentValue);
      setAssignmentValue("");
      setIsDisabled(true);
    };

    const submissionBody = {
      submissionLink: assignmentValue,
      videoId: videoInfo._id,
      courseId: courseInfo?._id,
    };
    if (assignmentValue !== "" && !submittedAssignment) {
      const submitRes = await api.submitAssignment(submissionBody);
      if (submitRes.data?.success === 201) {
        dispatch(services.successAlert(submitRes.data?.result));
        onSuccess();
      } else {
        dispatch(services.failureAlert(submitRes.data?.error));
      }
    } else if (assignmentValue !== "" && submittedAssignment) {
      if (assignmentValue === submittedAssignment) {
        onSuccess();
        return;
      }
      const updateRes = await api.updateassignmentsubmission(submissionBody);
      if (updateRes.data?.success === 200) {
        dispatch(services.successAlert(updateRes.data.result));
        onSuccess();
      } else {
        dispatch(services.failureAlert(updateRes.data?.error));
      }
    }
  };

  return (
    <aside className={styles["container"]}>
      {/* <div className="side-container footer-container"> */}
      <section className={styles["scores"]}>
        <header>
          <h3>Score</h3>
        </header>
        {!loading ? (
          <>
            <div className={styles["score"]}>
              <span>Video Watch Scores:</span> <span>{scores.watch}</span>
            </div>
            <div className={styles["score"]}>
              <span>Assignments Scores:</span> <span>{scores.assignment}</span>
            </div>
            <div className={styles["score"]}>
              <span>Assignment in evaluation:</span>{" "}
              <span>{scores.pending}</span>
            </div>
            <div className={styles["score"]}>
              <span>Total Scores:</span> <span>{scores.total}</span>
            </div>
          </>
        ) : (
          <sk.RowCardSkeleton number="4" type="sk-assignment-lines" />
        )}
      </section>
      <section className={styles["guidelines"]}>
        <header>
          <h3>Assignment</h3>
        </header>
        <article>
          {!loading ? (
            <>
              {data?.details ? (
                <div onClick={() => window.open(data.details)}>
                  {data.details}
                </div>
              ) : (
                <>
                  <li>Rewrite the code taught by the mentor in the video.</li>
                  <li>Push/upload the code to a new GitHub repository.</li>
                  <li>
                    Name the repository using the current lecture name followed
                    by "CipherSchools." For example, Lecture1-CipherSchools.
                  </li>
                  <li>Submit the repository olnk below.</li>
                  <li>Submit the repository link in the input box below.</li>
                  <li>
                    Repeat steps 1-4 for each video lecture in the course.
                  </li>
                  <li>
                    Failure to complete this process may affect your overall
                    scores.
                  </li>
                  <li>
                    If you are new to Git and GitHub, we recommend watching the
                    video by clicking here.
                    <br />
                    <a href="https://www.cipherschools.com/videos/all-about-git-and-github-to-get-started-11ad">
                      https://www.cipherschools.com/videos/all-about-git-and-github-to-get-started-11ad
                    </a>
                  </li>
                </>
              )}
            </>
          ) : (
            <>
              <sk.RowCardSkeleton number="5" type="sk-assignment-lines" />
              <sk.RowCardSkeleton number="1" type="sk-input-box" />
            </>
          )}
        </article>
      </section>
      <footer className={styles["submission"]}>
        {submittedAssignment && (
          <article className={styles.submissionBox}>
            <div className={[styles["prev"]]}>
              <h4>Your Submission</h4>
              <p>
                <a href={submittedAssignment}>{submittedAssignment}</a>
              </p>
            </div>
            <p>
              <span>Remarks:</span>{" "}
              {remarks.length ? remarks : "Evaluation pending!"}
            </p>

            {showEdit && (
              <button
                onClick={() => {
                  setAssignmentValue(submittedAssignment);
                  setIsDisabled(false);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                >
                  <path
                    fill="currentColor"
                    d="M13.8 2.2a2.51 2.51 0 0 0-3.54 0l-6.9 6.91l-1.76 3.62a1.26 1.26 0 0 0 1.12 1.8a1.23 1.23 0 0 0 .55-.13l3.62-1.76l6-6l.83-.82l.06-.06a2.52 2.52 0 0 0 .02-3.56zm-.89.89a1.25 1.25 0 0 1 0 1.77l-1.77-1.77a1.24 1.24 0 0 1 .86-.37a1.22 1.22 0 0 1 .91.37zM2.73 13.27L4.29 10L6 11.71zm4.16-2.4L5.13 9.11L10.26 4L12 5.74z"
                  />
                </svg>
              </button>
            )}
          </article>
        )}
        <form>
          <input
            id="ca-input-field"
            type="text"
            disabled={isDisabled}
            value={assignmentValue}
            onChange={(e) => setAssignmentValue(e.target.value)}
            placeholder="Enter your github assignment link"
          />
          <button
            disabled={isDisabled}
            onClick={handleSubmit}
            id="vd_Assignment_Submit"
          >
            <svg
              width="18"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.568 9.94977L3.03601 11.2058C2.94942 11.2202 2.86815 11.2572 2.80039 11.313C2.73263 11.3689 2.68077 11.4415 2.65001 11.5238L0.0530096 18.4808C-0.19499 19.1208 0.47401 19.7308 1.08801 19.4238L19.088 10.4238C19.2127 10.3615 19.3175 10.2658 19.3908 10.1472C19.4641 10.0287 19.503 9.89212 19.503 9.75277C19.503 9.61342 19.4641 9.47682 19.3908 9.3583C19.3175 9.23978 19.2127 9.14402 19.088 9.08177L1.08801 0.0817693C0.47401 -0.225231 -0.19499 0.385769 0.0530096 1.02477L2.65101 7.98177C2.68162 8.06418 2.73343 8.13707 2.8012 8.19307C2.86897 8.24908 2.9503 8.28623 3.03701 8.30077L10.569 9.55577C10.6154 9.56389 10.6574 9.58809 10.6876 9.62413C10.7179 9.66016 10.7345 9.70571 10.7345 9.75277C10.7345 9.79983 10.7179 9.84538 10.6876 9.88141C10.6574 9.91745 10.6154 9.94165 10.569 9.94977H10.568Z"
                fill="white"
              />
            </svg>
          </button>
        </form>
      </footer>
      {/* </div> */}
    </aside>
  );
}

export default React.memo(Assignments);
